import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSettingsBackend,
  getDeckBytes,
  removeInteractionMarker,
  upsertInteractionMarker,
  type DeckFetchProgress,
} from '@slideforge/host-bridge';
import { buildFidelityReport, parseDeck, type ParseResult } from '@slideforge/ooxml';
import {
  CourseStore,
  emptyCourseData,
  type CourseData,
  type FidelityReport,
  type Interaction,
  type Namespace,
  type Quiz,
  type SlideRef,
  type StoreEvent,
} from '@slideforge/shared';
import { createLocalSidecarClient } from '../sidecar/client.js';

export type DeckStatus = 'idle' | 'reading' | 'ready' | 'error';

/** What the author needs to know about whether their work is safe. */
export type SaveStatus = 'clean' | 'pending' | 'saved' | 'error';

export interface DeckState {
  readonly status: DeckStatus;
  readonly parsed?: ParseResult;
  readonly fidelity?: FidelityReport;
  readonly progress?: DeckFetchProgress;
  readonly error?: string;
  readonly byteLength: number;
}

/** Where the pane remembers which slide was selected, across reloads. */
const SELECTION_KEY = 'slideforge.ui.selectedSlide';

/**
 * The add-in's single source of state.
 *
 * Reading the deck is deliberately explicit rather than automatic on open: it
 * takes 10-40 seconds on Office on the web (SPIKE 1), and a task pane that
 * freezes for that long on open reads as a crashed add-in.
 */
export function useSlideForge(courseId = 'slideforge-course') {
  const [deck, setDeck] = useState<DeckState>({ status: 'idle', byteLength: 0 });
  const [course, setCourse] = useState<CourseData>(() => emptyCourseData(courseId));
  const [storeEvents, setStoreEvents] = useState<StoreEvent[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('clean');
  const [chosenSlideId, setChosenSlideId] = useState<string | undefined>(() =>
    readRememberedSelection(courseId),
  );
  const abortRef = useRef<AbortController | undefined>(undefined);

  const store = useMemo(
    () => new CourseStore({
      settings: createSettingsBackend(),
      sidecar: createLocalSidecarClient(),
      courseId,
    }),
    [courseId],
  );

  /**
   * Selection survives a pane reload.
   *
   * PowerPoint tears the task pane's iframe down and rebuilds it freely — on
   * close/reopen, and on some window operations. React state does not survive
   * that, so the previously selected slide came back as `undefined` and the
   * quiz editor fell back to "no slide selected" with the author's work
   * apparently gone. It was never gone; nothing was pointing at it.
   */
  const setSelectedSlideId = useCallback(
    (next: string | undefined) => {
      setChosenSlideId(next);
      try {
        if (next) globalThis.localStorage?.setItem(`${SELECTION_KEY}.${courseId}`, next);
        else globalThis.localStorage?.removeItem(`${SELECTION_KEY}.${courseId}`);
      } catch {
        // Private mode, or storage disabled by policy. Selection just stops
        // being sticky; nothing else depends on it.
      }
    },
    [courseId],
  );

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = store.subscribe((event) => {
      setStoreEvents((previous) => [...previous.slice(-9), event]);
      if (event.kind === 'changed') {
        setCourse(event.data);
        setSaveStatus('pending');
      }
      if (event.kind === 'saved') setSaveStatus('saved');
      if (event.kind === 'error') setSaveStatus('error');
    });

    void store
      .getCourse()
      .then((data) => {
        if (!cancelled) setCourse(data);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setStoreEvents((previous) => [
          ...previous,
          {
            kind: 'error',
            code: 'corrupt-blob',
            message: error instanceof Error ? error.message : String(error),
          },
        ]);
      });

    return () => {
      cancelled = true;
      unsubscribe();
      void store.flush().catch(() => undefined);
    };
  }, [store]);

  /**
   * Force the debounced write out before the pane can be torn down.
   *
   * Writes coalesce over 800ms so a keystroke is not a `saveAsync`. That window
   * is also exactly long enough to lose the last edit if the author closes the
   * pane, switches document, or closes PowerPoint immediately after typing —
   * the unmount cleanup runs too late to help, because the iframe is already
   * going away. `pagehide` and the hidden transition are the last points at
   * which a flush can still land.
   */
  useEffect(() => {
    const flush = () => void store.flush().catch(() => undefined);
    const onVisibility = () => {
      if (globalThis.document?.visibilityState === 'hidden') flush();
    };

    globalThis.addEventListener?.('pagehide', flush);
    globalThis.addEventListener?.('beforeunload', flush);
    globalThis.document?.addEventListener?.('visibilitychange', onVisibility);

    return () => {
      globalThis.removeEventListener?.('pagehide', flush);
      globalThis.removeEventListener?.('beforeunload', flush);
      globalThis.document?.removeEventListener?.('visibilitychange', onVisibility);
    };
  }, [store]);

  const readDeck = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setDeck({ status: 'reading', byteLength: 0 });

    const result = await getDeckBytes({
      signal: controller.signal,
      onProgress: (progress) =>
        setDeck((previous) => ({ ...previous, status: 'reading', progress, byteLength: progress.bytesReceived })),
    });

    if (!result.ok) {
      setDeck({ status: 'error', error: result.message, byteLength: 0 });
      return;
    }

    try {
      const parsed = await parseDeck(result.value.bytes);
      const fidelity = buildFidelityReport({ deck: parsed.deck, diagnostics: parsed.diagnostics });
      setDeck({
        status: 'ready',
        parsed,
        fidelity,
        byteLength: result.value.bytes.byteLength,
      });

      // Remember the slide list so the next pane open can show authored work
      // immediately, without a second 10-40 second read.
      const slideIndex: SlideRef[] = parsed.deck.slides.map((slide) => ({
        id: slide.id,
        index: slide.index,
        name: slide.name,
      }));
      await store.updateCourse((draft) => {
        draft.slideIndex = slideIndex;
      });

    } catch (error) {
      setDeck({
        status: 'error',
        error: error instanceof Error ? error.message : String(error),
        byteLength: result.value.bytes.byteLength,
      });
    }
  }, [store]);

  /**
   * The slide list the UI works from: the freshly parsed deck when there is
   * one, otherwise what was remembered last time. Authoring surfaces bind to
   * this rather than to `deck.parsed`, so stored quizzes and narration stay
   * reachable in a pane that has not read the file yet.
   */
  const slides = useMemo<readonly SlideRef[]>(() => {
    if (deck.parsed) {
      return deck.parsed.deck.slides.map((slide) => ({
        id: slide.id,
        index: slide.index,
        name: slide.name,
      }));
    }
    return [...course.slideIndex].sort((a, b) => a.index - b.index);
  }, [deck.parsed, course.slideIndex]);

  /** True when the slide list is remembered rather than freshly parsed. */
  const slidesAreRemembered = !deck.parsed && slides.length > 0;

  /**
   * The effective selection, derived rather than synchronised.
   *
   * A remembered id can name a slide that has since been deleted from the deck.
   * Correcting that in an effect means rendering once with a dangling selection
   * and then again with a good one; deriving it means the invalid state never
   * reaches the UI at all.
   */
  const selectedSlideId = useMemo(() => {
    if (slides.length === 0) return chosenSlideId;
    if (chosenSlideId && slides.some((s) => s.id === chosenSlideId)) return chosenSlideId;
    return slides[0]?.id;
  }, [slides, chosenSlideId]);

  const setSlideData = useCallback(
    async (slideId: string, namespace: Namespace, value: unknown) => {
      await store.setSlideData(slideId, namespace, value);
    },
    [store],
  );

  const setQuiz = useCallback(
    async (slideId: string, quiz: Quiz | undefined) => {
      if (quiz) await store.setSlideData(slideId, 'quiz', quiz);
      else await store.clearSlideData(slideId, 'quiz');
    },
    [store],
  );

  const setInteraction = useCallback(
    async (slideId: string, interaction: Interaction | undefined) => {
      if (interaction) {
        await store.setSlideData(slideId, 'interaction', interaction);
        return upsertInteractionMarker(slideId, interaction.title, interaction.type);
      }
      await store.clearSlideData(slideId, 'interaction');
      return removeInteractionMarker(slideId);
    },
    [store],
  );

  const updateCourse = useCallback(
    async (mutator: (draft: CourseData) => void) => {
      await store.updateCourse(mutator);
    },
    [store],
  );

  const quizFor = useCallback(
    (slideId: string | undefined): Quiz | undefined =>
      slideId ? course.slides[slideId]?.quiz : undefined,
    [course],
  );

  const interactionFor = useCallback(
    (slideId: string | undefined): Interaction | undefined =>
      slideId ? course.slides[slideId]?.interaction : undefined,
    [course],
  );

  /** Writes anything pending immediately. Bound to the "save now" affordance. */
  const flushNow = useCallback(async () => {
    try {
      await store.flush();
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [store]);

  return {
    deck,
    course,
    store,
    storeEvents,
    saveStatus,
    slides,
    slidesAreRemembered,
    selectedSlideId,
    setSelectedSlideId,
    readDeck,
    setSlideData,
    setQuiz,
    setInteraction,
    updateCourse,
    quizFor,
    interactionFor,
    flushNow,
  };
}

function readRememberedSelection(courseId: string): string | undefined {
  try {
    return globalThis.localStorage?.getItem(`${SELECTION_KEY}.${courseId}`) ?? undefined;
  } catch {
    return undefined;
  }
}

export type SlideForgeState = ReturnType<typeof useSlideForge>;
